import { defineStore } from 'pinia'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import EstateProjectAPI from '@/modules/estate-project/constants/estate-project-api-uri-const'
import {
  createEstateProjectTableConfig,
  formatTableDataEstateProject,
} from '@/modules/estate-project/helper/table'
import { formatEstateProjectToSubmit } from '@/modules/estate-project/helper/estate-project-form'

export enum EstateProjectListActionTypes {
  PENDING = 'PENDING',
  FILTERING = 'FILTERING',
  FETCHING = 'FETCHING',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
  CREATING = 'CREATING',
  CREATE_SUCCESS = 'CREATE_SUCCESS',
  CREATE_FAILURE = 'CREATE_FAILURE',
}

interface EstateProjectListTabItem {
  id: string | number
  label: string
  active: boolean
}

interface EstateProjectListStoreState {
  action_type: EstateProjectListActionTypes
  action_mess: string
  uuid: string
  data: {
    list: any[]
    totalRecords: number
    totalPage: number
  }
  filter: Array<{
    key: string
    type: string
    label: string
    placeholder: string
    value: any
    options?: Array<{ label: string; value: string }>
  }>
  pageSize: number
  page: number
  tabs: EstateProjectListTabItem[]
  tableConfig: any
}

const createTableConfig = () => createEstateProjectTableConfig()

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>
  return {}
}

const toNumber = (value: unknown) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const toDisplayText = (value: unknown, fallback = '--') => {
  if (typeof value === 'string' && value.trim()) return value
  if (typeof value === 'number') return String(value)
  return fallback
}

const normalizeListItem = (value: unknown) => {
  const item = toRecord(value)
  const owner = toRecord(item.owner)

  return {
    ...item,
    project_name: toDisplayText(item.project_name ?? item.name),
    project_address: toDisplayText(item.project_address ?? item.address),
    created_by_name: toDisplayText(item.created_by_name ?? owner.name),
    created_at: typeof item.created_at === 'string' ? item.created_at : '',
  }
}

const normalizeListPayload = (payload: unknown) => {
  const safePayload = toRecord(payload)
  const root = toRecord(safePayload.data)
  const nested = toRecord(root.data)

  const rawList = Array.isArray(root.data)
    ? root.data
    : Array.isArray(nested.data)
      ? nested.data
      : Array.isArray(nested.items)
        ? nested.items
        : Array.isArray(root.items)
          ? root.items
          : []

  const totalRecords = toNumber(nested.total) || toNumber(root.total) || rawList.length
  const totalPage =
    toNumber(nested.last_page) || toNumber(root.last_page) || (rawList.length > 0 ? 1 : 0)

  return {
    list: rawList.map(normalizeListItem),
    totalRecords,
    totalPage,
  }
}

export const useEstateProjectListStore = defineStore('estate-project.list', {
  state: (): EstateProjectListStoreState => ({
    action_type: EstateProjectListActionTypes.PENDING,
    action_mess: '',
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'keyword',
        type: 'text',
        label: 'Tìm kiếm dự án',
        placeholder: 'Nhập tên dự án / địa chỉ',
        value: '',
      },
      {
        key: 'status',
        type: 'select-multiple',
        label: 'Trạng thái',
        placeholder: 'Chọn trạng thái',
        value: [],
        options: [
          { label: 'Hoạt động', value: 'active' },
          { label: 'Nháp', value: 'draft' },
        ],
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách dự án bất động sản',
        active: true,
      },
    ],
    tableConfig: createTableConfig(),
  }),
  actions: {
    changeTab(targetId: string | number) {
      for (const tab of this.tabs) {
        tab.active = tab.id === targetId
      }
    },
    closeTab(targetId: string | number) {
      const index = this.tabs.findIndex((tab) => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
      if (!this.tabs.some((tab) => tab.active)) {
        this.changeTab('list')
      }
    },
    closeAllTab() {
      this.tabs = [
        {
          id: 'list',
          label: 'Danh sách dự án bất động sản',
          active: true,
        },
      ]
    },
    newTab(item: EstateProjectListTabItem) {
      const exists = this.tabs.some((tab) => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },
    setFilter(filter: EstateProjectListStoreState['filter']) {
      this.filter = filter
      this.page = 1
      this.action_type = EstateProjectListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePage(page: number) {
      this.page = page
      this.action_type = EstateProjectListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize(size: number) {
      this.pageSize = size
      this.page = 1
      this.action_type = EstateProjectListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      }
    },
    async fetchList() {
      this.action_type = EstateProjectListActionTypes.FETCHING
      try {
        const res = await APIClient.get(EstateProjectAPI.project.list, {
          params: {
            page: this.page,
            pageSize: this.pageSize,
          },
        })

        const normalized = normalizeListPayload(res)

        this.data = {
          list: formatTableDataEstateProject(normalized.list || []),
          totalRecords: normalized.totalRecords,
          totalPage: normalized.totalPage,
        }
        this.action_type = EstateProjectListActionTypes.FETCH_SUCCESS
      } catch (e: any) {
        this.action_mess = e?.message || 'Không thể tải danh sách dự án'
        this.action_type = EstateProjectListActionTypes.FETCH_FAILURE
        throw e
      }
    },
    async createProject(values: any) {
      this.action_type = EstateProjectListActionTypes.CREATING
      try {
        const payload = formatEstateProjectToSubmit(values)
        const res = await APIClient.post(EstateProjectAPI.project.create, payload)

        const safeRes = toRecord(res)
        const rootData = toRecord(safeRes.data)
        const nestedData = toRecord(rootData.data)

        const createdId = toNumber(rootData.id) || toNumber(nestedData.id) || toNumber(safeRes.id)

        this.action_type = EstateProjectListActionTypes.CREATE_SUCCESS
        return {
          id: createdId,
        }
      } catch (e: any) {
        this.action_mess = e?.message || 'Không thể tạo dự án'
        this.action_type = EstateProjectListActionTypes.CREATE_FAILURE
        throw e
      }
    },
  },
})
